import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Badge, Spinner, Button, Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import {
    fetchAllergyActionPlans,
    selectPlans,
    selectMeta,
    selectLoading,
    selectError
} from '../../store/allergyActionPlan/allergyActionPlanSlice';
import { getEffectiveStudentId } from '../../utilities/teacherModulePermissions';
import { formatDisplayDate } from '../../utilities/displayFormat';
import DOMPurify from 'isomorphic-dompurify';

const AllergyActionPlanListComp = ({ data, viewMode }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const plans = useSelector(selectPlans);
    const meta = useSelector(selectMeta);
    const loading = useSelector(selectLoading);
    const error = useSelector(selectError);
    let profileData = useSelector((state) => state.setting.setting.user_data);
    profileData = profileData ? JSON.parse(profileData) : null;

    const [currentPage, setCurrentPage] = useState(1);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);

    useEffect(() => {
        dispatch(fetchAllergyActionPlans({ page: currentPage, limit: 10, student_id: getEffectiveStudentId(profileData) }));
    }, [dispatch, currentPage]);

    const handleViewDetails = (plan) => {
        setSelectedPlan(plan);
        setShowDetailModal(true);
    };

    const handleCloseModal = () => {
        setShowDetailModal(false);
        setSelectedPlan(null);
    };

    const formatDate = (dateString) => {
        if (!dateString || dateString === '1899-11-29') return 'N/A';
        return formatDisplayDate(dateString);
    };

    // Defensive check and definition for displayData - the standalone list
    // screen (viewMode !== 'profile') shows every plan, active and
    // inactive alike (each card's own badge already makes status clear);
    // only the profile-embedded view stays scoped to active ones, since
    // that one is answering "is this step of the packet done", not
    // browsing history.
    const filteredData = viewMode === 'profile' ? (Array.isArray(data) ? data.filter(item => item.status === 1) : []) : [];
    const displayData = viewMode === 'profile' ? filteredData : (Array.isArray(plans) ? plans : []);
    if (viewMode === 'profile' && (!filteredData || filteredData.length === 0)) {
        return (
            <Container>
                <div className="text-center p-5">
                    <p className="text-muted">No active Allergy Action Plan. Please add new data.</p>
                    <Link to="/dashboard/allergy-action-plan/add">
                        <Button variant="primary">Create Your First Plan</Button>
                    </Link>
                </div>
            </Container>
        );
    }
    return (
        <Container fluid className="px-0">
            {/* Scoped styles matching the No Peanut Policy / Lunch Plan
                Waiver list screens. Cards are now full-width (one per row,
                see the Col below) rather than a multi-column grid, so
                there's room for symptoms/treatment and both signatures
                without clamping/shrinking. */}
            <style>{`
                .allergy-plan-card {
                    border: 1px solid #eef0f3;
                    border-radius: 12px;
                    transition: transform 0.15s ease, box-shadow 0.15s ease;
                }
                .allergy-plan-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 24px rgba(17, 24, 39, 0.08) !important;
                }
                .allergy-plan-signature-box {
                    height: 76px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #f8f9fa;
                    border: 1px dashed #dcdfe3;
                    border-radius: 8px;
                    overflow: hidden;
                }
            `}</style>
            {loading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" role="status" style={{ color: '#6f42c1' }}>
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                    <p className="mt-3 text-muted mb-0">Loading action plans...</p>
                </div>
            ) : error ? (
                <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                    <Card.Body className="text-center py-5">
                        <i className="icon material-symbols-outlined" style={{ fontSize: '48px', color: '#dc3545' }}>error</i>
                        <p className="text-danger mt-2 mb-0">{error}</p>
                    </Card.Body>
                </Card>
            ) : displayData.length === 0 ? (
                <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                    <Card.Body className="text-center py-5">
                        <i className="icon material-symbols-outlined" style={{ fontSize: '64px', color: '#ccc' }}>health_and_safety</i>
                        <p className="mt-3 text-muted">No allergy action plans available</p>
                        <Link to="/dashboard/allergy-action-plan/add">
                            <Button variant="primary">Create Your First Plan</Button>
                        </Link>
                    </Card.Body>
                </Card>
            ) : (
                <>
                    <Row className="g-3">
                        {/* If viewMode is not 'profile', use plans from Redux, else use filteredData from props */}
                        {displayData.map((plan) => (
                            <Col xs="12" key={plan.id}>
                                <Card className="allergy-plan-card shadow-sm">
                                    <Card.Body>
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                            <div className="d-flex align-items-center gap-3">
                                                <h6 className="mb-0 fw-bold text-dark">Action Plan #{plan.id}</h6>
                                                <Badge
                                                    bg={plan.status === 1 ? "success" : "secondary"}
                                                    className="rounded-pill"
                                                    style={{ fontSize: '11px', fontWeight: 500, padding: '3px 10px', lineHeight: 1.3 }}
                                                >
                                                    {plan.status === 1 ? "Active" : "Inactive"}
                                                </Badge>
                                            </div>
                                            <div className="d-flex align-items-center gap-3 flex-shrink-0">
                                                <div className="d-flex gap-2">
                                                    <Button
                                                        variant="outline-info"
                                                        size="sm"
                                                        className="d-inline-flex align-items-center justify-content-center"
                                                        onClick={() => handleViewDetails(plan)}
                                                    >
                                                        <i className="icon material-symbols-outlined me-1" style={{ fontSize: '16px' }}>visibility</i>
                                                        View
                                                    </Button>
                                                    {plan.status === 1 && (
                                                        <Button
                                                            variant="primary"
                                                            size="sm"
                                                            className="d-inline-flex align-items-center justify-content-center"
                                                            onClick={() => navigate(`/dashboard/allergy-action-plan/edit/${plan.id}`)}
                                                        >
                                                            <i className="icon material-symbols-outlined me-1" style={{ fontSize: '16px' }}>edit</i>
                                                            Edit
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="mb-3">
                                            {plan.is_peanuts === 1 && <Badge bg="warning" className="me-1 mb-1">Peanuts</Badge>}
                                            {plan.is_shellfish === 1 && <Badge bg="warning" className="me-1 mb-1">Shellfish</Badge>}
                                            {plan.is_tree_nuts === 1 && <Badge bg="warning" className="me-1 mb-1">Tree Nuts</Badge>}
                                            {plan.is_fish === 1 && <Badge bg="warning" className="me-1 mb-1">Fish</Badge>}
                                            {plan.is_milk === 1 && <Badge bg="warning" className="me-1 mb-1">Milk</Badge>}
                                            {plan.is_food_additives === 1 && <Badge bg="warning" className="me-1 mb-1">Food Additives</Badge>}
                                            {plan.require_epi_pen === 1 && <Badge bg="danger" className="me-1 mb-1">EpiPen Required</Badge>}
                                        </div>

                                        <Row className="g-4 align-items-start">
                                            <Col md="3">
                                                <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                                    Symptoms
                                                </div>
                                                <div style={{ fontSize: '14px' }}>
                                                    {plan.symptoms || <span className="text-muted fst-italic">Not provided</span>}
                                                </div>
                                            </Col>
                                            <Col md="3">
                                                <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                                    Emergency Treatment
                                                </div>
                                                <div style={{ fontSize: '14px' }}>
                                                    {plan.emergency_treatment_procedures || <span className="text-muted fst-italic">Not provided</span>}
                                                </div>
                                            </Col>
                                            <Col md="3">
                                                <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                                    Parent Signature
                                                </div>
                                                <div className="allergy-plan-signature-box">
                                                    {plan.parent_signature ? (
                                                        <img
                                                            src={plan.parent_signature}
                                                            alt="Parent Signature"
                                                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                                        />
                                                    ) : (
                                                        <span className="text-muted" style={{ fontSize: '11px' }}>No signature</span>
                                                    )}
                                                </div>
                                            </Col>
                                            <Col md="3">
                                                <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                                    Supervisor Signature
                                                </div>
                                                <div className="allergy-plan-signature-box">
                                                    {plan.supervisor_signature ? (
                                                        <img
                                                            src={plan.supervisor_signature}
                                                            alt="Supervisor Signature"
                                                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                                        />
                                                    ) : (
                                                        <span className="text-muted" style={{ fontSize: '11px' }}>No signature</span>
                                                    )}
                                                </div>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>

                    {/* Pagination */}
                    {meta && meta.last_page > 1 && (
                        <div className="d-flex justify-content-center mt-4">
                            <nav>
                                <ul className="pagination">
                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => setCurrentPage(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        >
                                            Previous
                                        </button>
                                    </li>
                                    {[...Array(meta.last_page)].map((_, index) => (
                                        <li
                                            key={index + 1}
                                            className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                                        >
                                            <button
                                                className="page-link"
                                                onClick={() => setCurrentPage(index + 1)}
                                            >
                                                {index + 1}
                                            </button>
                                        </li>
                                    ))}
                                    <li className={`page-item ${currentPage === meta.last_page ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => setCurrentPage(currentPage + 1)}
                                            disabled={currentPage === meta.last_page}
                                        >
                                            Next
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    )}
                </>
            )}

            {/* Detail Modal - size="xl" (was "lg") so the full-width
                sections below actually use the available space instead of
                cramming a 2-column layout into a narrow modal. */}
            <Modal
                show={showDetailModal}
                onHide={handleCloseModal}
                centered
                size="xl"
            >
                <Modal.Header closeButton>
                    <Modal.Title className="d-flex align-items-center gap-2">
                        Allergy Action Plan #{selectedPlan?.id} Details
                        {selectedPlan && (
                            <Badge bg={selectedPlan.status === 1 ? "success" : "secondary"} className="rounded-pill">
                                {selectedPlan.status === 1 ? "Active" : "Inactive"}
                            </Badge>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                    {selectedPlan && (
                        <div>
                            {/* Overview - fields that used to be missing from this
                                modal entirely (submitted date, form type). */}
                            <Row className="g-3 mb-4">
                                <Col md="6">
                                    <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Submitted</div>
                                    {/* This form has no created_at column - parent_signature_date
                                        (required at submission time) is the closest real signal
                                        for when it was filled out. */}
                                    <div style={{ fontSize: '14px' }}>{formatDate(selectedPlan.parent_signature_date)}</div>
                                </Col>
                                <Col md="6">
                                    <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Form Type</div>
                                    <div className="d-flex gap-2">
                                        {selectedPlan.is_allergy_form === 1 && <Badge bg="info" className="rounded-pill">Allergies</Badge>}
                                        {selectedPlan.is_preference_form === 1 && <Badge bg="info" className="rounded-pill">Preferences</Badge>}
                                        {selectedPlan.is_allergy_form !== 1 && selectedPlan.is_preference_form !== 1 && <span className="text-muted">N/A</span>}
                                    </div>
                                </Col>
                            </Row>

                            {/* Allergies */}
                            <div className="mb-4 p-3" style={{ background: '#f8f9fa', border: '1px solid #eef0f3', borderRadius: '10px' }}>
                                <div className="d-flex align-items-center mb-3">
                                    <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '32px', height: '32px', backgroundColor: '#6f42c1' }}>
                                        <i className="material-symbols-outlined" style={{ fontSize: '18px' }}>restaurant</i>
                                    </div>
                                    <h6 className="mb-0 fw-bold">Allergies</h6>
                                </div>
                                <Row className="g-3">
                                    <Col md="3" xs="6">
                                        <div className="text-muted" style={{ fontSize: '12px' }}>Peanuts</div>
                                        <div style={{ fontSize: '14px' }}>{selectedPlan.is_peanuts === 1 ? 'Yes' : 'No'}</div>
                                    </Col>
                                    <Col md="3" xs="6">
                                        <div className="text-muted" style={{ fontSize: '12px' }}>Shellfish</div>
                                        <div style={{ fontSize: '14px' }}>
                                            {selectedPlan.is_shellfish === 1 ? 'Yes' : 'No'}
                                            {selectedPlan.shellfish_specify && <small className="text-muted"> ({selectedPlan.shellfish_specify})</small>}
                                        </div>
                                    </Col>
                                    <Col md="3" xs="6">
                                        <div className="text-muted" style={{ fontSize: '12px' }}>Tree Nuts</div>
                                        <div style={{ fontSize: '14px' }}>{selectedPlan.is_tree_nuts === 1 ? 'Yes' : 'No'}</div>
                                    </Col>
                                    <Col md="3" xs="6">
                                        <div className="text-muted" style={{ fontSize: '12px' }}>Fish</div>
                                        <div style={{ fontSize: '14px' }}>
                                            {selectedPlan.is_fish === 1 ? 'Yes' : 'No'}
                                            {selectedPlan.fish_specify && <small className="text-muted"> ({selectedPlan.fish_specify})</small>}
                                        </div>
                                    </Col>
                                    <Col md="3" xs="6">
                                        <div className="text-muted" style={{ fontSize: '12px' }}>Milk</div>
                                        <div style={{ fontSize: '14px' }}>{selectedPlan.is_milk === 1 ? 'Yes' : 'No'}</div>
                                    </Col>
                                    <Col md="3" xs="6">
                                        <div className="text-muted" style={{ fontSize: '12px' }}>Food Additives</div>
                                        <div style={{ fontSize: '14px' }}>
                                            {selectedPlan.is_food_additives === 1 ? 'Yes' : 'No'}
                                            {selectedPlan.food_additives_specify && <small className="text-muted"> ({selectedPlan.food_additives_specify})</small>}
                                        </div>
                                    </Col>
                                    <Col md="3" xs="6">
                                        <div className="text-muted" style={{ fontSize: '12px' }}>All Dairy Products</div>
                                        <div style={{ fontSize: '14px' }}>{selectedPlan.all_dairy_products === 1 ? 'Yes' : 'No'}</div>
                                    </Col>
                                    <Col md="3" xs="6">
                                        <div className="text-muted" style={{ fontSize: '12px' }}>Other</div>
                                        <div style={{ fontSize: '14px' }}>
                                            {selectedPlan.is_other === 1 ? 'Yes' : 'No'}
                                            {selectedPlan.other_specify && <small className="text-muted"> ({selectedPlan.other_specify})</small>}
                                        </div>
                                    </Col>
                                </Row>
                            </div>

                            {/* Insect Stings/Bites */}
                            <div className="mb-4 p-3" style={{ background: '#f8f9fa', border: '1px solid #eef0f3', borderRadius: '10px' }}>
                                <div className="d-flex align-items-center mb-3">
                                    <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '32px', height: '32px', backgroundColor: '#6f42c1' }}>
                                        <i className="material-symbols-outlined" style={{ fontSize: '18px' }}>pest_control</i>
                                    </div>
                                    <h6 className="mb-0 fw-bold">Insect Stings/Bites</h6>
                                </div>
                                <Row className="g-3">
                                    <Col md="4">
                                        <div className="text-muted" style={{ fontSize: '12px' }}>Has Allergy</div>
                                        <div style={{ fontSize: '14px' }}>{selectedPlan.insect_stings_bites === 1 ? 'Yes' : 'No'}</div>
                                    </Col>
                                    {selectedPlan.insect_stings_bites === 1 && (
                                        <>
                                            <Col md="4">
                                                <div className="text-muted" style={{ fontSize: '12px' }}>Specify</div>
                                                <div style={{ fontSize: '14px' }}>{selectedPlan.insect_stings_bites_specify || 'N/A'}</div>
                                            </Col>
                                            <Col md="4">
                                                <div className="text-muted" style={{ fontSize: '12px' }}>Medication</div>
                                                <div style={{ fontSize: '14px' }}>{selectedPlan.insect_stings_bites_medication || 'N/A'}</div>
                                            </Col>
                                        </>
                                    )}
                                </Row>
                            </div>

                            {/* Symptoms & Treatment */}
                            <div className="mb-4 p-3" style={{ background: '#fff5f5', border: '1px solid #f5c2c7', borderRadius: '10px' }}>
                                <div className="d-flex align-items-center mb-3">
                                    <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '32px', height: '32px', backgroundColor: '#dc3545' }}>
                                        <i className="material-symbols-outlined" style={{ fontSize: '18px' }}>emergency</i>
                                    </div>
                                    <h6 className="mb-0 fw-bold">Symptoms & Emergency Treatment</h6>
                                </div>
                                <Row className="g-3">
                                    <Col md="6">
                                        <div className="text-muted" style={{ fontSize: '12px' }}>Symptoms</div>
                                        <div style={{ fontSize: '14px' }}>{selectedPlan.symptoms || 'N/A'}</div>
                                    </Col>
                                    <Col md="6">
                                        <div className="text-muted" style={{ fontSize: '12px' }}>Emergency Treatment Procedures</div>
                                        <div style={{ fontSize: '14px' }}>{selectedPlan.emergency_treatment_procedures || 'N/A'}</div>
                                    </Col>
                                </Row>
                            </div>

                            {/* EpiPen */}
                            <div className="mb-4 p-3" style={{ background: '#f8f9fa', border: '1px solid #eef0f3', borderRadius: '10px' }}>
                                <div className="d-flex align-items-center mb-3">
                                    <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '32px', height: '32px', backgroundColor: '#6f42c1' }}>
                                        <i className="material-symbols-outlined" style={{ fontSize: '18px' }}>medication</i>
                                    </div>
                                    <h6 className="mb-0 fw-bold">EpiPen Information</h6>
                                </div>
                                <Row className="g-3">
                                    <Col md="4">
                                        <div className="text-muted" style={{ fontSize: '12px' }}>Requires EpiPen</div>
                                        <div style={{ fontSize: '14px' }}>{selectedPlan.require_epi_pen === 1 ? 'Yes' : 'No'}</div>
                                    </Col>
                                    {selectedPlan.require_epi_pen === 1 && (
                                        <Col md="4">
                                            <div className="text-muted" style={{ fontSize: '12px' }}>Location of EpiPen</div>
                                            <div style={{ fontSize: '14px' }}>{selectedPlan.location_of_epi_pen || 'N/A'}</div>
                                        </Col>
                                    )}
                                    {selectedPlan.require_epi_pen === 1 && selectedPlan.location_of_epi_pen_at_daycare && (
                                        <Col md="4">
                                            <div className="text-muted" style={{ fontSize: '12px' }}>Location of EpiPen at Daycare</div>
                                            <div style={{ fontSize: '14px' }}>{selectedPlan.location_of_epi_pen_at_daycare}</div>
                                        </Col>
                                    )}
                                </Row>
                            </div>

                            {/* Signatures */}
                            <div>
                                <div className="d-flex align-items-center mb-3">
                                    <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '32px', height: '32px', backgroundColor: '#6f42c1' }}>
                                        <i className="material-symbols-outlined" style={{ fontSize: '18px' }}>draw</i>
                                    </div>
                                    <h6 className="mb-0 fw-bold">Signatures</h6>
                                </div>
                                <Row className="g-3">
                                    <Col md="6">
                                        <div className="text-muted mb-1" style={{ fontSize: '12px' }}>Parent Signature</div>
                                        {selectedPlan.parent_signature ? (
                                            <div className="border rounded p-2 bg-light mb-2">
                                                <img
                                                    src={selectedPlan.parent_signature}
                                                    alt="Parent Signature"
                                                    style={{ maxWidth: '100%', maxHeight: '120px', display: 'block' }}
                                                />
                                            </div>
                                        ) : (
                                            <p className="text-muted">N/A</p>
                                        )}
                                        <small className="text-muted">Date: {formatDate(selectedPlan.parent_signature_date)}</small>
                                    </Col>
                                    <Col md="6">
                                        <div className="text-muted mb-1" style={{ fontSize: '12px' }}>Supervisor Signature</div>
                                        {selectedPlan.supervisor_signature ? (
                                            <div className="border rounded p-2 bg-light mb-2">
                                                <img
                                                    src={selectedPlan.supervisor_signature}
                                                    alt="Supervisor Signature"
                                                    style={{ maxWidth: '100%', maxHeight: '120px', display: 'block' }}
                                                />
                                            </div>
                                        ) : (
                                            <p className="text-muted">N/A</p>
                                        )}
                                        <small className="text-muted">Date: {formatDate(selectedPlan.supervisor_signature_date)}</small>
                                    </Col>
                                </Row>
                                {selectedPlan.supervisor_note && (
                                    <Row className="g-3 mt-1">
                                        <Col md="12">
                                            <div className="text-muted mb-1" style={{ fontSize: '12px' }}>Supervisor Note</div>
                                            <div style={{ fontSize: '14px' }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedPlan.supervisor_note) }} />
                                        </Col>
                                    </Row>
                                )}
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        Close
                    </Button>
                    {selectedPlan && selectedPlan.status === 1 && (
                        <Button
                            variant="primary"
                            onClick={() => {
                                handleCloseModal();
                                navigate(`/dashboard/allergy-action-plan/edit/${selectedPlan.id}`);
                            }}
                        >
                            <i className="material-symbols-outlined me-1" style={{ verticalAlign: 'middle', fontSize: '18px' }}>edit</i>
                            Edit Plan
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </Container>
    );
};
export default AllergyActionPlanListComp;
